// packages import
import { yupResolver } from '@hookform/resolvers/yup'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { Box, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Typography } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import { FC, useCallback, useContext, useEffect, useState } from 'react'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
// component import
import { Alert } from '../common/Alert'
import { CustomController } from '../common/CustomController'
// others
import {
  Address,
  CaseStatus,
  ExternalUser,
  Maybe,
  UpdateExternalUserAddress,
  useCreateAddressMutation,
  useUpdateExternalUserAddressMutation
} from '../../../generated'
import { NOT_APPLICABLE, US_STATES } from '../../constants'
import { CaseContext } from '../../context'
import { EditExternalUserAddressProps } from '../../interfaceTypes'
import { addNewExternalUserAddressValidationSchema } from '../../validationSchema'
import { ExternalUserActionType } from './externalUserReducer'
import CheckboxController from '../common/CheckboxController'
import { renderFieldData } from '../common/CommonExternalUserView'

export const EditExternalUserAddress: FC<EditExternalUserAddressProps> = ({
  addresses,
  externalUserTitle,
  refetch,
  resetFields,
  handleClose,
  externalUserId,
  externalUserDispatch,
  externalUserState
}) => {
  const [isEditOpen2, setIsEditOpen2] = useState<boolean>(false)
  const { currentCase } = useContext(CaseContext)
  const { status: caseStatus } = currentCase || {}
  const isClosed = caseStatus === CaseStatus.Completed
  const defaultState = US_STATES.filter(state => state.name === 'New Jersey')
  const addressTest = addresses && addresses[0]
  const [address, setAddress] = useState<Maybe<Address>>(addressTest)
  const { address1, address2, country, county, city, zipCode, state, id: addressId } = address || {}
  const { currentExternalUser } = externalUserState || {}
  const [international, setInternational] = useState(currentExternalUser?.isInternational)

  useEffect(() => {
    setAddress(addressTest!)
    // eslint-disable-next-line
  }, [addresses, refetch])

  const [updateExternalUserAddressMutation, { loading }] = useUpdateExternalUserAddressMutation({
    onError({ message }) {
      Alert.error(message)
    },

    onCompleted(data) {
      if (data) {
        const { updateExternalUserAddress } = data || {}
        const { externalUser } = updateExternalUserAddress || {}
        const { addresses: addressesData, isInternational } = externalUser || {}
        const newAddress = addressesData && addressesData[0]
        if (newAddress) setAddress(newAddress!)

        Alert.success('Address updated successfully')
        setIsEditOpen2(false)
        resetFields()
        externalUserDispatch &&
          externalUserDispatch({
            type: ExternalUserActionType.SET_CURRENT_EXTERNAL_USER,
            currentExternalUser: {
              ...currentExternalUser,
              addresses: [newAddress] as Address[],
              isInternational
            } as ExternalUser
          })
        refetch && refetch()
        setInternational(isInternational)
      }
    }
  })

  const [createAddressMutation, { loading: createAddressLoading }] = useCreateAddressMutation({
    onError({ message }) {
      Alert.error(message)
    },

    onCompleted(data) {
      if (data) {
        const { createAddress } = data || {}
        const { address: newAddress } = createAddress || {}
        if (newAddress) setAddress(newAddress)
        Alert.success('Address created successfully')

        handleClose && handleClose()
        setIsEditOpen2(false)
        resetFields()
        refetch && refetch()
      }
    }
  })

  const methods = useForm<UpdateExternalUserAddress>({
    mode: 'all',
    resolver: yupResolver(addNewExternalUserAddressValidationSchema),

    defaultValues: {
      address1: address1 || '',
      address2: address2 || '',
      country: country || 'USA',
      county: county || '',
      zipCode: zipCode || '',
      city: city || '',
      externalUserId: '',
      state: defaultState[0].name || '',
      isInternational: international
    }
  })

  const resetAddress = useCallback(() => {
    setValue('address1', address1 || '')
    setValue('address2', address2 || '')
    setValue('country', country || '')
    setValue('county', county || '')
    setValue('zipCode', zipCode || '')
    setValue('city', city || '')
    setValue('state', state || '')
    setValue('isInternational', international)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address1, address2, country, county, zipCode, city, state, international])

  useEffect(() => {
    resetAddress()
    // eslint-disable-next-line
  }, [])

  const { handleSubmit, control, setValue, watch } = methods

  const onSubmit: SubmitHandler<UpdateExternalUserAddress> = async (data: UpdateExternalUserAddress) => {
    const { address1, address2, zipCode, city, country, county, state, isInternational } = data
    setInternational(isInternational)
    if (addressId) {
      await updateExternalUserAddressMutation({
        variables: {
          updateExternalUserAddress: {
            id: addressId,
            address1: address1 || '',
            address2: address2 || '',
            city: city || '',
            country: country || '',
            county: county || '',
            zipCode: zipCode || '',
            state: state || '',
            isInternational
          }
        }
      })
    } else {
      await createAddressMutation({
        variables: {
          createAddressInput: {
            address1: address1 || '',
            address2: address2 || '',
            city: city || '',
            country: country || '',
            county: county || '',
            zipCode: zipCode || '',
            state: state || '',
            externalUserId: externalUserId || ''
          }
        }
      })
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display="flex" justifyContent="space-between" style={{ alignItems: 'baseline' }}>
          <Typography variant="body2" fontSize="18px" sx={{ fontWeight: 'medium', marginBottom: '0.875rem' }}>
            {externalUserTitle}
          </Typography>

          <Box>
            <IconButton
              sx={{ p: 0 }}
              aria-label={isEditOpen2 ? 'back' : 'edit'}
              color="primary"
              size="large"
              onClick={() => {
                setIsEditOpen2(!isEditOpen2)
                resetFields()
                resetAddress()
              }}
              disabled={isClosed}
            >
              {isEditOpen2 ? <ArrowBackIcon /> : <EditIcon />}
            </IconButton>

            {isEditOpen2 && (
              <IconButton color="primary" type="submit" disabled={loading || createAddressLoading} aria-label="save">
                <SaveIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        <Collapse in={!isEditOpen2}>
          <Box mt={2}>
            <Grid container spacing={2} rowSpacing={2}>
              <Grid item md={12} sm={12} xs={12}>
                <Typography variant="body2" fontSize="14px">
                  International:
                </Typography>
                {renderFieldData(international ? `Yes` : 'No')}
              </Grid>

              <Grid item md={6} sm={12} xs={12}>
                <Box>
                  <Typography variant="body2" fontSize="14px">
                    Address Line 1:
                  </Typography>
                  <Typography fontSize="16px" variant="h6" sx={{ fontWeight: 'normal' }}>
                    {address1 || NOT_APPLICABLE}
                  </Typography>
                </Box>
              </Grid>

              <Grid item md={6} sm={12} xs={12}>
                <Box>
                  <Typography variant="body2" fontSize="14px">
                    Address Line 2:
                  </Typography>
                  <Typography variant="h6" fontSize="16px" sx={{ fontWeight: 'normal' }}>
                    {address2 || NOT_APPLICABLE}
                  </Typography>
                </Box>
              </Grid>

              <Grid item md={3} sm={12} xs={12}>
                <Box>
                  <Typography variant="body2" fontSize="14px">
                    City:
                  </Typography>
                  <Typography variant="h6" fontSize="16px" sx={{ fontWeight: 'normal' }}>
                    {city || NOT_APPLICABLE}
                  </Typography>
                </Box>
              </Grid>

              {!currentExternalUser?.isInternational && !international && (
                <Grid item md={3} sm={12} xs={12}>
                  <Box>
                    <Typography variant="body2" fontSize="14px">
                      State:
                    </Typography>
                    <Typography variant="h6" fontSize="16px" sx={{ fontWeight: 'normal' }}>
                      {state || NOT_APPLICABLE}
                    </Typography>
                  </Box>
                </Grid>
              )}

              <Grid item md={3} sm={12} xs={12}>
                <Box>
                  <Typography variant="body2" fontSize="14px">
                    Country:
                  </Typography>
                  <Typography variant="h6" fontSize="16px" sx={{ fontWeight: 'normal' }}>
                    {country || NOT_APPLICABLE}
                  </Typography>
                </Box>
              </Grid>

              <Grid item md={3} sm={12} xs={12}>
                <Box>
                  <Typography variant="body2" fontSize="14px">
                    Zip Code:
                  </Typography>
                  <Typography variant="h6" fontSize="16px" sx={{ fontWeight: 'normal' }}>
                    {zipCode?.padStart(5, '0') || NOT_APPLICABLE}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        <Collapse in={isEditOpen2}>
          <Grid container spacing={2} rowSpacing={2}>
            <Grid item md={12} sm={12} xs={12}>
              <CheckboxController name="isInternational" label="International" />
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <CustomController controllerName="address1" controllerLabel="Address line 1" fieldType="text" />
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <CustomController controllerName="address2" controllerLabel="Address line 2" fieldType="text" />
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <CustomController controllerName="city" controllerLabel="City" fieldType="text" />
            </Grid>

            {!watch('isInternational') && (
              <Grid item md={6} sm={12} xs={12}>
                <FormControl variant="outlined" fullWidth sx={{ marginTop: 1 }}>
                  <InputLabel id="address-state">State</InputLabel>

                  <Controller
                    name="state"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="address-state"
                        label="State"
                        fullWidth
                        MenuProps={{ style: { maxHeight: 300 } }}
                      >
                        {US_STATES.map((item, index) => {
                          const { name } = item || {}
                          return (
                            <MenuItem value={name} key={`${item}-${index}`}>
                              {name}
                            </MenuItem>
                          )
                        })}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
            )}

            <Grid item md={6} sm={12} xs={12}>
              <CustomController controllerName="country" controllerLabel="Country" fieldType="text" />
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <CustomController controllerName="zipCode" controllerLabel="Zip Code" fieldType="text" />
            </Grid>
          </Grid>
        </Collapse>
      </form>
    </FormProvider>
  )
}
